// src/pages/sales/SalesSummary.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Popover,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  TableRestaurant as TableRestaurantIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/cashier/Header';
import Footer from '../../../components/RestaurantPOS/cashier/Footer';
import Preloader from '../../../theme/Preloader';
import '../../../styles/Waiters.css';

// Chart.js imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 10;

const LOCATIONS = [
  { id: 5, name: 'African', color: '#2E7D32', icon: '🌍' },
  { id: 3, name: 'Continental', color: '#1565C0', icon: '🌐' },
  { id: 4, name: 'Lounge', color: '#6A1B9A', icon: '🍸' },
];

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: '#FF9800', icon: <PendingIcon /> },
  IN_PROGRESS: { label: 'In Progress', color: '#2196F3', icon: <ScheduleIcon /> },
  READY: { label: 'Ready', color: '#4CAF50', icon: <CheckCircleIcon /> },
  COMPLETED: { label: 'Completed', color: '#2E7D32', icon: <CheckCircleIcon /> },
  CANCELLED: { label: 'Cancelled', color: '#D32F2F', icon: <CancelIcon /> },
};

const PAYMENT_STATUS_CONFIG = {
  UNPAID: { label: 'Unpaid', color: '#F44336' },
  PAID: { label: 'Paid', color: '#4CAF50' },
  PARTIAL: { label: 'Partial', color: '#FF9800' },
  REFUNDED: { label: 'Refunded', color: '#9E9E9E' },
};

const ORDER_TYPE_CONFIG = {
  DINE_IN: { label: 'Dine In', icon: <TableRestaurantIcon /> },
  TAKEAWAY: { label: 'Takeaway', icon: <PeopleIcon /> },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (!amount && amount !== 0) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const formatDateInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status) => {
  return STATUS_CONFIG[status]?.color || '#9E9E9E';
};

const getStatusLabel = (status) => {
  return STATUS_CONFIG[status]?.label || status || 'Unknown';
};

const getPaymentStatusColor = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.color || '#9E9E9E';
};

const getPaymentStatusLabel = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.label || status || 'Unknown';
};

const getOrderTypeLabel = (type) => {
  return ORDER_TYPE_CONFIG[type]?.label || type || 'Unknown';
};

const getOrderTypeIcon = (type) => {
  return ORDER_TYPE_CONFIG[type]?.icon || <TableRestaurantIcon />;
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[SalesSummary] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[SalesSummary] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[SalesSummary] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[SalesSummary] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[SalesSummary] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[SalesSummary] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  if (!endpoint.includes('?') && !url.endsWith('/')) {
    url = url + '/';
  }
  
  console.debug(`[SalesSummary] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[SalesSummary] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[SalesSummary] API Error:', err.message);
    throw err;
  }
};

// ============================================
// CUSTOM DATE RANGE PICKER COMPONENT
// ============================================

const CustomDateRangePicker = React.memo(({ dateRange, onDateRangeChange, locationColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(dateRange.startDate || null);
  const [tempEndDate, setTempEndDate] = useState(dateRange.endDate || null);

  useEffect(() => {
    setTempStartDate(dateRange.startDate || null);
    setTempEndDate(dateRange.endDate || null);
  }, [dateRange]);

  const handleApply = () => {
    onDateRangeChange({ startDate: tempStartDate, endDate: tempEndDate });
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onDateRangeChange({ startDate: null, endDate: null });
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    setTempStartDate(today);
    setTempEndDate(todayEnd);
    onDateRangeChange({ startDate: today, endDate: todayEnd });
    setIsOpen(false);
  };

  const handleQuickRange = (days) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    let start = new Date();
    start.setHours(0, 0, 0, 0);
    if (days === 0) {
      handleToday();
      return;
    } else {
      start.setDate(end.getDate() - days);
      start.setHours(0, 0, 0, 0);
    }
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleQuickMonth = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const hasDates = dateRange.startDate || dateRange.endDate;
  const isToday = dateRange.startDate && dateRange.endDate && 
    new Date(dateRange.startDate).toDateString() === new Date().toDateString() &&
    new Date(dateRange.endDate).toDateString() === new Date().toDateString();

  const quickRanges = [
    { label: 'Today', days: 'today', icon: <TodayIcon sx={{ fontSize: 16 }} /> },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Month', days: 'month' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant={hasDates ? 'contained' : 'outlined'}
          size="medium"
          onClick={() => {
            setTempStartDate(dateRange.startDate || null);
            setTempEndDate(dateRange.endDate || null);
            setIsOpen(true);
          }}
          startIcon={<DateRangeIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            ...(hasDates && {
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }),
            ...(!hasDates && {
              borderColor: alpha(locationColor, 0.3),
              color: locationColor,
            }),
          }}
        >
          {hasDates ? (
            isToday ? 'Today' : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
          ) : (
            'Select Date Range'
          )}
        </Button>

        {hasDates && (
          <IconButton
            size="small"
            onClick={() => {
              onDateRangeChange({ startDate: null, endDate: null });
            }}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
            maxWidth: '480px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Select Date Range
          </Typography>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
              Quick Select
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickRanges.map((range, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (range.days === 'today') {
                      handleToday();
                    } else if (range.days === 'month') {
                      handleQuickMonth();
                    } else {
                      handleQuickRange(range.days);
                    }
                  }}
                  startIcon={range.icon}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderColor: alpha(locationColor, 0.2),
                    '&:hover': { borderColor: locationColor },
                  }}
                >
                  {range.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
            Custom Range
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={tempStartDate ? formatDateInput(tempStartDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempStartDate(date);
                } else {
                  setTempStartDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                }
              }}
            />
            <TextField
              label="End Date"
              type="date"
              value={tempEndDate ? formatDateInput(tempEndDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempEndDate(date);
                } else {
                  setTempEndDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                }
              }}
            />
          </Box>

          {tempStartDate && tempEndDate && tempStartDate > tempEndDate && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              Start date must be before end date
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
            }}
          >
            Clear
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={tempStartDate && tempEndDate && tempStartDate > tempEndDate}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
              '&:disabled': { bgcolor: 'grey.300' },
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// ============================================
// STATS CARDS COMPONENT
// ============================================

const StatsCards = React.memo(({ stats, locationColor, isMobile }) => {
  const theme = useTheme();
  
  const statItems = [
    {
      label: 'Total Orders',
      value: stats.totalOrders || 0,
      icon: <ReceiptIcon />,
      color: locationColor,
      change: stats.orderChange,
    },
    {
      label: 'Total Revenue',
      value: formatCurrency(stats.totalRevenue || 0),
      icon: <AttachMoneyIcon />,
      color: '#2E7D32',
      change: stats.revenueChange,
    },
    {
      label: 'Completed',
      value: stats.completed || 0,
      icon: <CheckCircleIcon />,
      color: '#2E7D32',
    },
    {
      label: 'Pending',
      value: stats.pending || 0,
      icon: <PendingIcon />,
      color: '#FF9800',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled || 0,
      icon: <CancelIcon />,
      color: '#D32F2F',
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((stat, index) => (
        <Grid item xs={6} sm={6} md={isMobile ? 6 : 2.4} key={index}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: `1px solid ${alpha(stat.color, 0.15)}`,
              bgcolor: alpha(stat.color, 0.04),
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: theme.shadows[4],
              },
              height: '100%',
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(stat.color, 0.12),
                    color: stat.color,
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      color: stat.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                  >
                    {stat.label}
                  </Typography>
                  {stat.change !== undefined && stat.change !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                      {stat.change >= 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 12, color: '#2E7D32' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 12, color: '#D32F2F' }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: stat.change >= 0 ? '#2E7D32' : '#D32F2F',
                          fontSize: '0.6rem',
                        }}
                      >
                        {stat.change >= 0 ? '+' : ''}{stat.change.toFixed(1)}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// ============================================
// FILTER BAR COMPONENT
// ============================================

const FilterBar = React.memo(({
  filters,
  onFilterChange,
  onSearch,
  onRefresh,
  isFetching,
  locationColor,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilters, setTempFilters] = useState(filters);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempFilters(filters);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    onFilterChange(tempFilters);
    handleFilterClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      status: 'ALL',
      paymentStatus: 'ALL',
      orderType: 'ALL',
      search: searchValue,
    };
    setTempFilters(resetFilters);
    onFilterChange(resetFilters);
    handleFilterClose();
  };

  const handleTempChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      onSearch(value);
    }, 400);
  };

  const filterCount = Object.values(filters).filter(v => 
    v !== 'ALL' && v !== '' && v !== null && v !== undefined
  ).length;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 2,
      mb: 3,
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      <TextField
        size="small"
        placeholder="Search by order number, customer, or table..."
        value={searchValue}
        onChange={handleSearchChange}
        sx={{
          flex: 1,
          minWidth: '200px',
          maxWidth: { xs: '100%', sm: '400px' },
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: 'white',
            '& fieldset': { borderColor: alpha(locationColor, 0.2) },
            '&:hover fieldset': { borderColor: locationColor },
            '&.Mui-focused fieldset': { borderColor: locationColor },
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          endAdornment: searchValue && (
            <InputAdornment position="end">
              <IconButton 
                size="small" 
                onClick={() => {
                  setSearchValue('');
                  onSearch('');
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleFilterClick}
          startIcon={<FilterAltIcon />}
          endIcon={filterCount > 0 ? <Badge badgeContent={filterCount} color="primary" /> : null}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: alpha(locationColor, 0.3),
            color: locationColor,
            '&:hover': {
              borderColor: locationColor,
              bgcolor: alpha(locationColor, 0.05),
            },
          }}
        >
          Filters
        </Button>

        <Tooltip title="Refresh Data">
          <IconButton
            onClick={onRefresh}
            disabled={isFetching}
            sx={{
              bgcolor: alpha(locationColor, 0.08),
              '&:hover': { bgcolor: alpha(locationColor, 0.15) },
              borderRadius: '10px',
              width: 40,
              height: 40,
            }}
          >
            {isFetching ? <CircularProgress size={22} /> : <RefreshIcon sx={{ color: locationColor }} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Export Data">
          <IconButton
            sx={{
              bgcolor: alpha(locationColor, 0.08),
              '&:hover': { bgcolor: alpha(locationColor, 0.15) },
              borderRadius: '10px',
              width: 40,
              height: 40,
            }}
          >
            <GetAppIcon sx={{ color: locationColor }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
            minWidth: { xs: 'calc(100vw - 32px)', sm: '360px' },
            maxWidth: '400px',
            boxShadow: (theme) => theme.shadows[12],
          }
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Filter Orders
        </Typography>

        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Order Status</InputLabel>
            <Select
              value={tempFilters.status || 'ALL'}
              onChange={(e) => handleTempChange('status', e.target.value)}
              label="Order Status"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={tempFilters.paymentStatus || 'ALL'}
              onChange={(e) => handleTempChange('paymentStatus', e.target.value)}
              label="Payment Status"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Payment Status</MenuItem>
              {Object.entries(PAYMENT_STATUS_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ color: config.color }}>●</span>
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Order Type</InputLabel>
            <Select
              value={tempFilters.orderType || 'ALL'}
              onChange={(e) => handleTempChange('orderType', e.target.value)}
              label="Order Type"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              {Object.entries(ORDER_TYPE_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResetFilters}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: locationColor,
                '&:hover': { bgcolor: locationColor, opacity: 0.9 },
              }}
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
});

// ============================================
// CHART COMPONENTS
// ============================================

const SalesChart = React.memo(({ data, locationColor, title, type = 'bar' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
            weight: 'bold',
          },
          boxWidth: isMobile ? 10 : 15,
          padding: isMobile ? 8 : 15,
        },
      },
    },
    scales: type === 'pie' ? undefined : {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: isMobile ? 8 : 11,
            weight: 'bold',
          },
          callback: function(value) {
            return '₦' + value.toLocaleString();
          }
        },
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 8 : 11,
            weight: 'bold',
          },
          maxRotation: isMobile ? 45 : 0,
        },
      },
    },
  };

  const ChartComponent = type === 'pie' ? Pie : type === 'line' ? Line : Bar;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha(locationColor, 0.1)}`,
        height: '100%',
        minHeight: 280,
      }}
    >
      <CardContent>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: locationColor }}>
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 200 : 250 }}>
          <ChartComponent data={data} options={chartOptions} />
        </Box>
      </CardContent>
    </Card>
  );
});

// ============================================
// ORDER TABLE ROW COMPONENT
// ============================================

const OrderTableRow = React.memo(({ order, locationColor, onViewDetails }) => {
  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch (e) { items = []; }

  const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: alpha(locationColor, 0.02) },
        transition: 'background-color 0.15s ease',
      }}
      onClick={() => onViewDetails(order)}
    >
      <TableCell>
        <Typography variant="body2" fontWeight={700}>
          {order.order_number}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(order.order_time)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {getOrderTypeLabel(order.order_type)}
        </Typography>
        {order.table_name && (
          <Typography variant="caption" color="text.secondary" display="block">
            {order.table_name}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {order.waiter_name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {itemCount} items
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.65rem',
            height: 24,
            border: `1px solid ${alpha(statusColor, 0.2)}`,
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={paymentStatusLabel}
          size="small"
          sx={{
            bgcolor: alpha(paymentStatusColor, 0.12),
            color: paymentStatusColor,
            fontWeight: 700,
            fontSize: '0.6rem',
            height: 22,
            border: `1px solid ${alpha(paymentStatusColor, 0.2)}`,
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="h6" fontWeight={700} color={locationColor} sx={{ fontSize: '1rem' }}>
          {formatCurrency(order.total_amount)}
        </Typography>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// ORDER DETAIL DIALOG COMPONENT
// ============================================

// ============================================
// ORDER DETAIL DIALOG COMPONENT - FIXED
// ============================================

const OrderDetailDialog = React.memo(({ open, order, onClose, locationColor }) => {
  // Move useCallback to the top level BEFORE any conditional returns
  const handlePrintReceipt = useCallback(() => {
    if (!order) return; // Check inside the function instead
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Please allow popups to print receipts');
      return;
    }
    
    let items = [];
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
    } catch (e) { items = []; }

    const statusLabel = getStatusLabel(order.order_status);
    const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
    const orderTypeLabel = getOrderTypeLabel(order.order_type);
    
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${order.order_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            padding: 20px;
            max-width: 350px;
            margin: 0 auto;
            background: white;
            color: #000;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 15px;
            margin-bottom: 15px;
          }
          .header h2 { font-size: 20px; margin-bottom: 5px; }
          .header .order-number { font-size: 16px; font-weight: bold; }
          .header .detail { font-size: 12px; color: #555; margin: 2px 0; }
          .items { margin: 15px 0; }
          .item-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px dotted #ddd;
            font-size: 13px;
          }
          .totals { margin-top: 15px; border-top: 2px dashed #000; padding-top: 10px; }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            font-size: 13px;
          }
          .grand-total {
            font-size: 18px;
            font-weight: bold;
            border-top: 2px solid #000;
            padding-top: 10px;
            margin-top: 5px;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px dashed #000;
            font-size: 11px;
            color: #666;
          }
          .status {
            display: inline-block;
            padding: 2px 10px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            margin-top: 5px;
          }
          @media print {
            .no-print { display: none !important; }
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${order.location_name || 'Restaurant'}</h2>
          <div class="order-number">${order.order_number}</div>
          <div class="detail">${formatDateTime(order.order_time)}</div>
          <div class="detail">${orderTypeLabel}</div>
          ${order.table_name ? `<div class="detail">Table: ${order.table_name}</div>` : ''}
          ${order.waiter_name ? `<div class="detail">Waiter: ${order.waiter_name}</div>` : ''}
          <div>
            <span class="status" style="background:${getStatusColor(order.order_status)}20;color:${getStatusColor(order.order_status)};border:1px solid ${getStatusColor(order.order_status)}">
              ${statusLabel}
            </span>
            <span class="status" style="background:${getPaymentStatusColor(order.payment_status)}20;color:${getPaymentStatusColor(order.payment_status)};border:1px solid ${getPaymentStatusColor(order.payment_status)}">
              ${paymentStatusLabel}
            </span>
          </div>
        </div>

        <div class="items">
          <div style="display:flex;justify-content:space-between;font-weight:bold;padding:5px 0;border-bottom:2px solid #000;font-size:13px;">
            <span>Item</span>
            <span>Qty</span>
            <span>Price</span>
          </div>
          ${items.map(item => `
            <div class="item-row">
              <span>${item.name}</span>
              <span>×${item.quantity || 1}</span>
              <span>${formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
            </div>
          `).join('')}
        </div>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>${formatCurrency(order.subtotal)}</span>
          </div>
          ${order.tax > 0 ? `
            <div class="total-row">
              <span>Tax</span>
              <span>${formatCurrency(order.tax)}</span>
            </div>
          ` : ''}
          ${order.discount > 0 ? `
            <div class="total-row">
              <span>Discount</span>
              <span>-${formatCurrency(order.discount)}</span>
            </div>
          ` : ''}
          <div class="total-row grand-total">
            <span><strong>TOTAL</strong></span>
            <span><strong>${formatCurrency(order.total_amount)}</strong></span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Thank you!</strong></p>
          <p>${new Date().toLocaleString()}</p>
        </div>
        
        <button class="no-print" style="display:block;margin:20px auto;padding:10px 30px;cursor:pointer;" onclick="window.print()">🖨️ Print</button>
        <button class="no-print" style="display:block;margin:5px auto;padding:5px 20px;cursor:pointer;" onclick="window.close()">Close</button>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }, [order]); // Add order to dependency array

  // Now the conditional return comes AFTER all hooks
  if (!order) return null;

  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  } catch (e) { items = []; }

  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
  const orderTypeLabel = getOrderTypeLabel(order.order_type);
  const OrderTypeIcon = getOrderTypeIcon(order.order_type);

  const totalItems = items.reduce((sum, i) => sum + (i.quantity || 1), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        borderRadius: '20px 20px 0 0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ReceiptIcon sx={{ color: locationColor, fontSize: 28 }} />
          <Typography variant="h5" component="span" fontWeight={700}>
            {order.order_number}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.12),
              color: statusColor,
              fontWeight: 700,
            }}
          />
          <Chip
            label={paymentStatusLabel}
            size="small"
            sx={{
              bgcolor: alpha(paymentStatusColor, 0.12),
              color: paymentStatusColor,
              fontWeight: 700,
            }}
          />
        </Box>
        <IconButton onClick={onClose} sx={{ bgcolor: alpha('#000', 0.04) }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{
          p: 3,
          bgcolor: alpha(locationColor, 0.02),
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Order Type
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {OrderTypeIcon}
              {orderTypeLabel}
            </Typography>
          </Box>
          
          {order.table_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Table
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.table_name}
              </Typography>
            </Box>
          )}
          
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Waiter
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {order.waiter_name || 'N/A'}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Order Time
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatDateTime(order.order_time)}
            </Typography>
          </Box>

          {order.customer_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Customer
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.customer_name}
                {order.customer_phone && ` (${order.customer_phone})`}
              </Typography>
            </Box>
          )}

          {order.location_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                Location
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {order.location_name}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
            <Typography variant="h6" fontWeight={700}>
              Order Items ({totalItems})
            </Typography>
          </Box>

          <Box sx={{
            bgcolor: alpha('#000', 0.02),
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 100px',
              gap: 1,
              p: 2,
              bgcolor: alpha('#000', 0.04),
              fontWeight: 700,
              fontSize: '0.8rem',
              color: 'text.secondary',
              borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            }}>
              <Typography variant="caption" fontWeight={700}>Item</Typography>
              <Typography variant="caption" fontWeight={700} align="center">Qty</Typography>
              <Typography variant="caption" fontWeight={700} align="right">Amount</Typography>
            </Box>

            {items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 100px',
                  gap: 1,
                  p: 2,
                  borderBottom: idx < items.length - 1 ? `1px solid ${alpha('#000', 0.04)}` : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: alpha(locationColor, 0.02) },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {item.name}
                  </Typography>
                  {item.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                      * {item.notes}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" fontWeight={600} align="center">
                  ×{item.quantity || 1}
                </Typography>
                <Typography variant="body2" fontWeight={700} align="right" color={locationColor}>
                  {formatCurrency((item.price || 0) * (item.quantity || 1))}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{
            mt: 3,
            pt: 2,
            borderTop: `2px solid ${alpha('#000', 0.06)}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
          }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Subtotal
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(order.subtotal)}
              </Typography>
            </Box>
            {order.tax > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Tax
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(order.tax)}
                </Typography>
              </Box>
            )}
            {order.discount > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Discount
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#D32F2F">
                  -{formatCurrency(order.discount)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, pt: 1, borderTop: `1px solid ${alpha('#000', 0.06)}` }}>
              <Typography variant="h6" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="h6" fontWeight={700} color={locationColor}>
                {formatCurrency(order.total_amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: 3,
        pb: 3,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            px: 4,
          }}
        >
          Close
        </Button>
        {order.order_status !== 'CANCELLED' && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintReceipt}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }}
          >
            Print Receipt
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});
// ============================================
// MAIN COMPONENT - SalesSummary
// ============================================

const SalesSummary = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const locationConfig = LOCATIONS.find(l => 
    l.name.toLowerCase() === locationKey.toLowerCase()
  ) || LOCATIONS[0];
  const LOCATION_COLOR = locationConfig.color;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const getTodayRange = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    return { startDate: today, endDate: todayEnd };
  }, []);

  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    paymentStatus: 'ALL',
    orderType: 'ALL',
  });
  
  const [dateRange, setDateRange] = useState(getTodayRange());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const isMountedRef = useRef(true);
  const fetchTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);

  // Chart data state
  const [chartData, setChartData] = useState({
    daily: { labels: [], datasets: [] },
    status: { labels: [], datasets: [] },
    payment: { labels: [], datasets: [] },
  });

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchOrders = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      console.debug(`[SalesSummary] Fetching sales data for ${LOCATION_NAME}...`);
      setIsFetching(true);

      const params = new URLSearchParams();
      params.append('limit', '500');
      params.append('skip', '0');

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = currentUser?.id;

      const locationId = filters.locationId !== 'ALL' ? filters.locationId : LOCATION_ID;
      if (locationId) {
        params.append('location_id', locationId);
      }

      // Filter by logged-in user
      if (userId) {
        params.append('waiter_id', userId);
      }

      if (filters.status !== 'ALL') {
        params.append('order_status', filters.status);
      }

      if (filters.paymentStatus !== 'ALL') {
        params.append('payment_status', filters.paymentStatus);
      }

      if (filters.orderType !== 'ALL') {
        params.append('order_type', filters.orderType);
      }

      if (dateRange.startDate) {
        const dateStr = formatDateForAPI(dateRange.startDate);
        if (dateStr) params.append('date_from', dateStr);
      }
      if (dateRange.endDate) {
        const dateStr = formatDateForAPI(dateRange.endDate);
        if (dateStr) params.append('date_to', dateStr);
      }

      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }

      const url = `/orders/history/?${params.toString()}`;
      console.debug('[SalesSummary] Fetching with URL:', url);

      const data = await apiRequest(url);
      console.debug('[SalesSummary] Sales data fetched:', data);

      if (isMountedRef.current) {
        let ordersList = [];
        if (data && typeof data === 'object') {
          if (Array.isArray(data.items)) {
            ordersList = data.items;
          } else if (Array.isArray(data)) {
            ordersList = data;
          }
        }

        console.debug(`[SalesSummary] Parsed ${ordersList.length} orders`);
        
        setOrders(ordersList);
        setFilteredOrders(ordersList);

        // Calculate stats
        const totalOrdersCount = ordersList.length;
        const totalRevenue = ordersList
          .filter(o => o.order_status === 'COMPLETED')
          .reduce((sum, o) => sum + (o.total_amount || 0), 0);
        const completed = ordersList.filter(o => o.order_status === 'COMPLETED').length;
        const pending = ordersList.filter(o => o.order_status === 'PENDING').length;
        const cancelled = ordersList.filter(o => o.order_status === 'CANCELLED').length;

        setStats({
          totalOrders: totalOrdersCount,
          totalRevenue: totalRevenue,
          completed,
          pending,
          cancelled,
        });

        // Generate chart data
        generateChartData(ordersList);
      }
    } catch (err) {
      console.error('[SalesSummary] Error fetching sales data:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch sales data: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [LOCATION_ID, LOCATION_NAME, filters, dateRange]);

  // ============================================
  // CHART DATA GENERATION
  // ============================================

  const generateChartData = useCallback((ordersList) => {
    // Daily sales data
    const dailyMap = {};
    const statusCounts = { PENDING: 0, IN_PROGRESS: 0, READY: 0, COMPLETED: 0, CANCELLED: 0 };
    const paymentCounts = { UNPAID: 0, PAID: 0, PARTIAL: 0, REFUNDED: 0 };

    ordersList.forEach(order => {
      const date = formatDate(order.order_date);
      if (!dailyMap[date]) {
        dailyMap[date] = { revenue: 0, orders: 0 };
      }
      dailyMap[date].revenue += order.total_amount || 0;
      dailyMap[date].orders += 1;

      if (order.order_status) {
        statusCounts[order.order_status] = (statusCounts[order.order_status] || 0) + 1;
      }
      if (order.payment_status) {
        paymentCounts[order.payment_status] = (paymentCounts[order.payment_status] || 0) + 1;
      }
    });

    const dates = Object.keys(dailyMap).sort();
    const revenues = dates.map(d => dailyMap[d].revenue);
    const orderCounts = dates.map(d => dailyMap[d].orders);

    // Daily chart
    setChartData(prev => ({
      ...prev,
      daily: {
        labels: dates,
        datasets: [
          {
            label: 'Revenue',
            data: revenues,
            backgroundColor: alpha(LOCATION_COLOR, 0.6),
            borderColor: LOCATION_COLOR,
            borderWidth: 2,
            borderRadius: 4,
          },
          {
            label: 'Orders',
            data: orderCounts,
            backgroundColor: alpha('#FF9800', 0.6),
            borderColor: '#FF9800',
            borderWidth: 2,
            borderRadius: 4,
          },
        ],
      },
      status: {
        labels: Object.keys(statusCounts).map(s => getStatusLabel(s)),
        datasets: [
          {
            data: Object.values(statusCounts),
            backgroundColor: Object.keys(statusCounts).map(s => getStatusColor(s)),
            borderColor: Object.keys(statusCounts).map(s => getStatusColor(s)),
            borderWidth: 2,
          },
        ],
      },
      payment: {
        labels: Object.keys(paymentCounts).map(p => getPaymentStatusLabel(p)),
        datasets: [
          {
            data: Object.values(paymentCounts),
            backgroundColor: Object.keys(paymentCounts).map(p => getPaymentStatusColor(p)),
            borderColor: Object.keys(paymentCounts).map(p => getPaymentStatusColor(p)),
            borderWidth: 2,
          },
        ],
      },
    }));
  }, [LOCATION_COLOR]);

  // ============================================
  // INITIALIZATION
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    const loadData = async () => {
      await fetchOrders();
      if (isMountedRef.current) {
        setIsLoading(false);
        isInitialMountRef.current = false;
      }
    };
    
    loadData();

    return () => {
      isMountedRef.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // ============================================
  // RE-FETCH WHEN DEPENDENCIES CHANGE
  // ============================================

  useEffect(() => {
    if (isInitialMountRef.current) return;
    
    if (isMountedRef.current) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      fetchTimeoutRef.current = setTimeout(() => {
        fetchOrders();
      }, 300);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [filters, dateRange, fetchOrders]);

  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      if (isTokenExpired(token)) {
        await refreshAuthToken();
      }
    };
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPage(0);
  }, []);

  const handleDateRangeChange = useCallback((newDateRange) => {
    console.debug('[SalesSummary] Date range changed:', newDateRange);
    setDateRange(newDateRange);
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchOrders();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
  }, [fetchOrders]);

  const handleViewDetails = useCallback((order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // ============================================
  // RENDER HELPERS
  // ============================================

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ============================================
  // LOADING STATE
  // ============================================

  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} Sales Summary...`} size={70} fullScreen={true} />;
  }

  // ============================================
  // DYNAMIC SIDEBAR
  // ============================================

  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/cashier/${locationKey}/Sidebar`).default;
  } catch (e) {
    Sidebar = require('../../../components/RestaurantPOS/cashier/african/Sidebar').default;
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu="Sales Summary" setActiveMenu={() => {}} />
          
          <div className="epos-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <Header />

            <Box sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, sm: 3 },
              bgcolor: '#f5f7fa',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
            }}>
              {/* Header Section */}
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '16px',
                p: { xs: 2, sm: 3 },
                mb: 3,
                border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}>
                  <Box>
                    <Typography variant="h4" sx={{
                      color: LOCATION_COLOR,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.5rem', sm: '2rem' },
                    }}>
                      📊 Sales Summary
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {filteredOrders.length} orders · {LOCATION_NAME}
                      {dateRange.startDate && dateRange.endDate && (
                        <span style={{ marginLeft: '8px', color: LOCATION_COLOR, fontWeight: 600 }}>
                          · {new Date(dateRange.startDate).toDateString() === new Date(dateRange.endDate).toDateString()
                            ? formatDate(dateRange.startDate)
                            : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
                          }
                        </span>
                      )}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<span>{locationConfig.icon}</span>}
                      label={LOCATION_NAME}
                      sx={{
                        bgcolor: alpha(LOCATION_COLOR, 0.1),
                        color: LOCATION_COLOR,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 1,
                        height: 36,
                        '& .MuiChip-label': { fontWeight: 700 },
                      }}
                    />
                   
                  </Box>
                </Box>
              </Box>

              {/* Stats Cards */}
              <StatsCards 
                stats={stats} 
                locationColor={LOCATION_COLOR} 
                isMobile={isMobile} 
              />

              {/* Date Range Picker */}
              <Box sx={{ mb: 3 }}>
                <CustomDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                  locationColor={LOCATION_COLOR}
                />
              </Box>

              {/* Charts Section */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                  <SalesChart
                    data={chartData.daily}
                    locationColor={LOCATION_COLOR}
                    title="Daily Sales Overview"
                    type="bar"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SalesChart
                    data={chartData.status}
                    locationColor={LOCATION_COLOR}
                    title="Order Status Distribution"
                    type="pie"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SalesChart
                    data={chartData.payment}
                    locationColor={LOCATION_COLOR}
                    title="Payment Status Distribution"
                    type="pie"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <SalesChart
                    data={chartData.daily}
                    locationColor={LOCATION_COLOR}
                    title="Sales Trend"
                    type="line"
                  />
                </Grid>
              </Grid>

              {/* Filters */}
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onRefresh={handleRefresh}
                isFetching={isFetching}
                locationColor={LOCATION_COLOR}
              />

              {/* Orders Table */}
              {isFetching && filteredOrders.length === 0 ? (
                <Box sx={{ py: 4 }}>
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px' }} />
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  border: `2px dashed ${alpha('#000', 0.08)}`,
                }}>
                  <ReceiptLongIcon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                    No Orders Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Try adjusting your filters or date range
                  </Typography>
                </Box>
              ) : (
                <Card elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(LOCATION_COLOR, 0.04) }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Order #</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Waiter</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Items</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Payment</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedOrders.map((order) => (
                          <OrderTableRow
                            key={order.id}
                            order={order}
                            locationColor={LOCATION_COLOR}
                            onViewDetails={handleViewDetails}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      borderTop: `1px solid ${alpha('#000', 0.06)}`,
                      '& .MuiTablePagination-select': { fontWeight: 600 },
                      '& .MuiTablePagination-displayedRows': { fontWeight: 600 },
                    }}
                  />
                </Card>
              )}
            </Box>
          </div>
        </div>
        <Footer />
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        open={detailDialogOpen}
        order={selectedOrder}
        onClose={handleCloseDetails}
        locationColor={LOCATION_COLOR}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          .MuiTableRow-root {
            transition: background-color 0.15s ease;
          }
          
          .MuiTableRow-root:hover {
            background-color: rgba(0,0,0,0.02);
          }
        `}
      </style>
    </>
  );
};

export default SalesSummary;